import type { FormEvent } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'
import AuthShell from './AuthShell'

export default function ForgotPassword() {
  const toast = useToast()
  const [email, setEmail] = useState('')
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})
    try {
      await api.post('/auth/forgot-password', { email })
      // The API deliberately answers the same way whether or not the address
      // has an account, so this screen must not reveal more than it does.
      setSent(true)
    } catch (err) {
      setErrors(fieldErrors(err))
      toast.error('Could not send the link', errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      heading="Reset your password"
      subheading="We will email you a link to choose a new one"
      tagline={
        <>
          Locked out? <br /> Let us get you back in.
        </>
      }
    >
      {sent ? (
        <div className="mt-7 space-y-4">
          <div className="rounded-lg border border-line bg-foreground/5 p-4 text-sm text-foreground/80">
            If that email has an account, a reset link is on its way. The link expires in 60 minutes.
          </div>
          <Link to="/login">
            <Button variant="outline" className="w-full">
              Back to sign in
            </Button>
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-7 space-y-4">
          <Field error={errors.email?.[0]} htmlFor="email">
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button type="submit" loading={loading}>
              Send link
            </Button>
            <Link to="/login">
              <Button variant="outline" className="w-full">
                Cancel
              </Button>
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  )
}
